import { useNavigate, useLocation } from 'react-router-dom';
import api, { resolveAssetUrl } from '../../utils/api';
import MainCard from 'components/MainCard';
import PageHeader from 'components/PageHeader';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Avatar,
  TableContainer
} from '@mui/material';
import { parseProductListResponse } from 'utils/parseListResponse';
import { pluralize } from 'utils/pluralize';
import DirhamSymbol from 'components/currency/DirhamSymbol';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import { getProductVariants, getProductCategoryName, PRODUCT_CURRENCY_SYMBOL_SCALE } from 'utils/productListHelpers';
import { dirhamSymbolEmSx } from 'utils/currency';
import { useCurrency } from 'contexts/CurrencyContext';

import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { saveAs } from 'file-saver';
import TableActionButton from 'components/table/TableActionButton';
import TableActions from 'components/table/TableActions';
import TableStatusChip from 'components/table/TableStatusChip';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { vegIndicatorSx, vegIndicatorDotSx, highlightCheckMarkSx } from 'components/table/tableChipStyles';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from 'contexts/ToastContext';
import ProductFilteredByStoreForm from 'pages/master/ProductFilteredByStoreForm';

const getImgUrl = (p) => {
  if (p?.image_url) return p.image_url;
  const d = p?.doc || p?.Doc;
  if (d?.imageUrl) return d.imageUrl;
  if (d?.attachment_url) return resolveAssetUrl(d.attachment_url);
  return null;
};

function VariantRows({ variants }) {
  const { currency } = useCurrency();
  if (!variants?.length) return null;
  return (
    <TableRow>
      <TableCell colSpan={11} sx={{ py: 0, bgcolor: 'grey.50', borderBottom: '2px solid', borderColor: 'primary.lighter' }}>
        <Collapse in timeout="auto" unmountOnExit>
          <Box sx={{ px: 5, py: 1.5 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              textTransform="uppercase"
              letterSpacing={0.5}
              mb={1}
              display="block"
            >
              Variants ({variants.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  {['#', 'Name', 'Price', 'Description', 'Description (Arabic)', 'Sequence'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        py: 0.8,
                        letterSpacing: 0.4
                      }}
                    >
                      {h === 'Price' ? (
                        <Stack direction="row" alignItems="baseline" spacing={0.35} component="span" sx={{ fontSize: '0.68rem' }}>
                          Price <DirhamSymbol sx={dirhamSymbolEmSx(PRODUCT_CURRENCY_SYMBOL_SCALE)} />
                        </Stack>
                      ) : (
                        h
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.map((v, i) => (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ py: 0.8, color: 'text.disabled', width: 36 }}>{i + 1}</TableCell>
                    <TableCell sx={{ py: 0.8, fontWeight: 600 }}>{v.name}</TableCell>
                    <TableCell sx={{ py: 0.8, color: 'primary.main', fontWeight: 700 }}>
                      <CurrencyAmount value={v.price} color="primary.main" fontWeight={700} decimals={2} />
                    </TableCell>
                    <TableCell sx={{ py: 0.8, color: 'text.secondary', maxWidth: 220 }}>{v.description || '—'}</TableCell>
                    <TableCell sx={{ py: 0.8, color: 'text.secondary', maxWidth: 220, direction: 'rtl' }}>{v.description_ar || '—'}</TableCell>
                    <TableCell sx={{ py: 0.8, color: 'text.disabled' }}>{v.sequence ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

function ProductRow({ p, idx, onEdit, onDelete, onToggleStatus, togglingId }) {
  const { currency } = useCurrency();
  const [expanded, setExpanded] = useState(false);
  const variants = getProductVariants(p);
  const hasVariants = variants.length > 0;
  const image = getImgUrl(p);

  return (
    <>
      <TableRow
        hover
        sx={{
          verticalAlign: 'middle',
          '& > *': { borderBottom: expanded ? 'unset' : undefined }
        }}
      >
        {/* # */}
        <TableCell sx={{ color: 'text.disabled', fontSize: 13, width: 44 }}>{idx + 1}</TableCell>

        {/* image */}
        <TableCell sx={{ width: 64 }}>
          <Avatar
            src={image || undefined}
            variant="rounded"
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.lighter',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            {!image && <RestaurantMenuIcon sx={{ fontSize: 20 }} />}
          </Avatar>
        </TableCell>

        {/* name + arabic */}
        <TableCell sx={{ minWidth: 180 }}>
          <Stack direction="row" alignItems="flex-start" gap={0.75}>
            {p.is_veg !== null && p.is_veg !== undefined && (
              <Tooltip title={p.is_veg ? 'Veg' : 'Non-Veg'}>
                <Box sx={vegIndicatorSx(p.is_veg)}>
                  <Box sx={vegIndicatorDotSx(p.is_veg)} />
                </Box>
              </Tooltip>
            )}
            <Box>
              <Typography fontWeight={600} variant="body2">
                {p.name}
              </Typography>
              {p.name_ar && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ direction: 'rtl' }}>
                  {p.name_ar}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {/* description */}
        <TableCell sx={{ minWidth: 160 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              maxWidth: 200
            }}
          >
            {p.description || '—'}
          </Typography>
          {p.description_ar && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: 200,
                direction: 'rtl'
              }}
            >
              {p.description_ar}
            </Typography>
          )}
        </TableCell>

        {/* category */}
        <TableCell sx={{ minWidth: 130 }}>
          <Typography variant="body2" color="text.secondary">
            {getProductCategoryName(p)}
          </Typography>
          {p.category?.name_ar && p.category?.name_en && (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ direction: 'rtl' }}>
              {p.category.name_ar}
            </Typography>
          )}
        </TableCell>

        {/* price */}
        <TableCell align="right" sx={{ minWidth: 90 }}>
          <CurrencyAmount value={p.price} color="primary.main" fontWeight={700} variant="body2" align="flex-end" decimals={2} />
        </TableCell>

        {/* variants */}
        <TableCell align="center" sx={{ minWidth: 72 }}>
          {hasVariants ? (
            <Tooltip title={expanded ? 'Hide variants' : `View ${variants.length} variant${variants.length > 1 ? 's' : ''}`}>
              <Chip
                label={variants.length}
                size="small"
                color="secondary"
                variant={expanded ? 'filled' : 'outlined'}
                onClick={() => setExpanded((prev) => !prev)}
                sx={{ cursor: 'pointer', fontWeight: 700, minWidth: 36 }}
              />
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          )}
        </TableCell>

        {/* highlight */}
        <TableCell align="center" sx={{ minWidth: 56 }}>
          {p.highlight ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={highlightCheckMarkSx}>
                <CheckRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
            </Box>
          ) : null}
        </TableCell>

        {/* sequence */}
        <TableCell align="center" sx={{ minWidth: 60 }}>
          <Typography variant="body2" color="text.secondary">
            {p.sequence ?? '—'}
          </Typography>
        </TableCell>

        {/* status toggle */}
        <TableCell align="center" sx={{ minWidth: 95 }}>
          <TableStatusChip
            active={!!p.status}
            loading={togglingId === p.id}
            onClick={() => togglingId !== p.id && onToggleStatus(p)}
            sx={{ minWidth: 90 }}
          />
        </TableCell>

        {/* actions */}
        <TableCell align="center" sx={{ minWidth: 90 }}>
          <TableActions>
            <TableActionButton action="edit" onClick={() => onEdit(p)} />
            <TableActionButton action="delete" onClick={() => onDelete(p)} />
          </TableActions>
        </TableCell>
      </TableRow>

      {expanded && <VariantRows variants={variants} />}
    </>
  );
}

const PAGE_LIMIT = 12;

export default function ProductList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, showApiError } = useToast();

  const params = new URLSearchParams(location.search);
  const storeId = params.get('store_id') || '';
  const categoryId = params.get('category_id') || '';
  const categoryName = params.get('category_name') || 'Products';
  const storeNameParam = params.get('store_name') || '';

  const [storeName, setStoreName] = useState(storeNameParam);
  const [categories, setCategories] = useState([]);
  const [filterCategoryId, setFilterCategoryId] = useState(categoryId);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [storeStatus, setStoreStatus] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);
  const [formModal, setFormModal] = useState({ open: false, id: null });

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const nextPageRef = useRef(1);
  const scrollRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreRef = useRef(() => {});

  useEffect(() => {
    if (!storeId) return;
    api
      .get(`/store/${storeId}`)
      .then((res) => {
        const d = res.data?.data;
        setStoreName(d?.title_en || d?.name_en || 'Store');
        setStoreStatus(d?.status ?? true);
      })
      .catch(() => {
        setStoreName('Store');
        setStoreStatus(true);
      });
  }, [storeId]);

  useEffect(() => {
    setFilterCategoryId(categoryId);
  }, [categoryId]);

  useEffect(() => {
    if (!storeId) {
      setCategories([]);
      return;
    }
    api
      .get('/product/category/list', { params: { store_id: storeId } })
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => setCategories([]));
  }, [storeId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const buildListParams = useCallback(
    (pageNum) => {
      const params = {
        store_id: storeId,
        page: pageNum,
        limit: PAGE_LIMIT
      };
      if (filterCategoryId) params.category_id = filterCategoryId;
      if (searchDebounced) {
        params.search = searchDebounced;
        params.q = searchDebounced;
      }
      return params;
    },
    [storeId, filterCategoryId, searchDebounced]
  );

  const fetchProductsPage = useCallback(
    async (pageNum) => {
      const res = await api.get('/product/list', { params: buildListParams(pageNum) });
      return parseProductListResponse(res);
    },
    [buildListParams]
  );

  const loadInitial = useCallback(async () => {
    if (!storeId || !filterCategoryId) {
      setLoading(false);
      setHasFetched(true);
      setProducts([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setHasMore(true);
    nextPageRef.current = 1;
    try {
      const result = await fetchProductsPage(1);
      setProducts(result.products);
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = 2;
    } catch {
      setProducts([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [storeId, filterCategoryId, fetchProductsPage]);

  const displayCategoryName = categories.find((c) => c.id === filterCategoryId)?.name_en || decodeURIComponent(categoryName || 'Products');

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (!storeId || !filterCategoryId || loading || loadingMore || !hasMore || loadMoreInFlightRef.current) {
      return;
    }

    const pageNum = nextPageRef.current;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const result = await fetchProductsPage(pageNum);
      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const added = result.products.filter((p) => !seen.has(p.id));
        return [...prev, ...added];
      });
      setTotal(result.total);
      setHasMore(result.currentPage < result.totalPages);
      nextPageRef.current = pageNum + 1;
    } catch (err) {
      showApiError(err, 'Failed to load more products');
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [storeId, filterCategoryId, loading, loadingMore, hasMore, fetchProductsPage, showApiError]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root, rootMargin: '40px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const handleToggleStatus = async (p) => {
    setTogglingId(p.id);
    const nextStatus = !p.status;
    try {
      await api.put(`/product/manage/status/${p.id}`, { status: nextStatus });
      setProducts((prev) => prev.map((r) => (r.id === p.id ? { ...r, status: nextStatus } : r)));
    } catch (err) {
      showApiError(err, 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/product/delete/${delDialog.id}`);
      setDelDialog({ open: false, id: null, name: '' });
      loadInitial();
    } catch (err) {
      showApiError(err, 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const openProductForm = (p = null) => {
    setFormModal({ open: true, id: p?.id ?? null });
  };

  const handleExport = async () => {
    if (!storeId) {
      showToast('Please select a store to export products', { severity: 'warning' });
      return;
    }
    setExportLoading(true);
    try {
      console.log("EXPORT URL", "/product/export");
      console.log("STORE ID", storeId);
      const response = await api.get('/product/export', {
        params: { store_id: storeId },
        responseType: 'blob'
      });

      let fileName = 'products.xlsx';
      const disposition = response.headers?.['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, '');
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, fileName);
      showToast('Products exported successfully', { severity: 'success' });
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          showToast(json.message || 'Failed to export products', { severity: 'error' });
        } catch {
          showApiError(err, 'Failed to export products');
        }
      } else {
        showApiError(err, 'Failed to export products');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseImportDialog = () => {
    if (importLoading) return;
    setImportOpen(false);
    setImportFile(null);
    setImportErrors([]);
    setImportStats(null);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      showToast('Please select an Excel file to import', { severity: 'error' });
      return;
    }
    if (!storeId) {
      showToast('Please select a store', { severity: 'error' });
      return;
    }

    setImportLoading(true);
    setImportErrors([]);
    setImportStats(null);

    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('store_id', storeId);

    try {
      console.log("IMPORT URL", "/product/import");
      console.log("STORE ID", storeId);
      const res = await api.post('/product/import', formData);
      const data = res.data || {};

      if (data.status === true) {
        const failedCount = Number(data.failed_count || 0);
        const successCount = Number(data.success_count || 0);
        const errors = Array.isArray(data.errors) ? data.errors : [];

        if (failedCount === 0 && errors.length === 0) {
          showToast(data.message || 'Products imported successfully', { severity: 'success' });
          handleCloseImportDialog();
          loadInitial();
        } else {
          setImportStats({
            total_rows: data.total_rows ?? 0,
            success_count: successCount,
            failed_count: failedCount
          });
          setImportErrors(errors);
          if (successCount > 0) {
            loadInitial();
          }
        }
      } else {
        showToast(data.message || 'Import failed', { severity: 'error' });
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setImportErrors(data.errors);
        }
      }
    } catch (err) {
      const resData = err?.response?.data;
      const msg = resData?.message || err?.message || 'Import failed';
      showToast(msg, { severity: 'error' });
      if (resData && Array.isArray(resData.errors)) {
        setImportErrors(resData.errors);
      }
    } finally {
      setImportLoading(false);
    }
  };

  const formatImportError = (err) => {
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err !== null) {
      if (err.row && (err.message || err.error)) {
        return `Row ${err.row} - ${err.message || err.error}`;
      }
      if (err.row && err.errors && Array.isArray(err.errors)) {
        return `Row ${err.row} - ${err.errors.join(', ')}`;
      }
      return err.message || err.error || JSON.stringify(err);
    }
    return String(err);
  };

  const COLS = [
    { label: '#', align: 'center' },
    { label: 'Image', align: 'left' },
    { label: 'Name', align: 'left' },
    { label: 'Description', align: 'left' },
    { label: 'Category', align: 'left' },
    { label: 'Price', align: 'right' },
    { label: 'Variants', align: 'center' },
    { label: 'Highlight', align: 'center' },
    { label: 'Seq', align: 'center' },
    { label: 'Status', align: 'center' },
    { label: 'Actions', align: 'center' }
  ];

  return (
    <Box>
      <PageHeader
        variant="nested"
        onBack={() => navigate(-1)}
        backTitle="Back to categories"
        title={displayCategoryName}
        subtitle={
          storeName ? `Store: ${decodeURIComponent(storeName)} — manage products in this category.` : 'Manage products in this category.'
        }
        count={total}
        titleExtra={
          !storeStatus ? <Chip label="Store Inactive" size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} /> : null
        }
        actions={
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
              onClick={handleExport}
              disabled={exportLoading}
              sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 2, py: 0.9 }}
            >
              {exportLoading ? 'Exporting…' : 'Export Excel'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => {
                setImportFile(null);
                setImportErrors([]);
                setImportStats(null);
                setImportOpen(true);
              }}
              sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 2, py: 0.9 }}
            >
              Import Excel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => openProductForm()}
              sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700, px: 2.25, py: 1 }}
            >
              Add Product
            </Button>
          </>
        }
      />

      {/* filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!storeId}>
          <InputLabel id="product-category-filter">Category</InputLabel>
          <Select
            labelId="product-category-filter"
            value={filterCategoryId}
            label="Category"
            onChange={(e) => setFilterCategoryId(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <CategoryIcon fontSize="small" color="action" />
              </InputAdornment>
            }
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name_en || c.name_ar || '—'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search by name, description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            )
          }}
          sx={{ maxWidth: 420, flex: 1 }}
        />
      </Box>

      {/* table */}
      <MainCard contentSX={{ p: 0 }}>
        {!storeId || !filterCategoryId ? (
          <Box sx={{ textAlign: 'center', p: 6, color: 'text.secondary' }}>
            <Typography variant="body1">Select a store and category to view products.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              ref={scrollRef}
              component={Paper}
              elevation={0}
              sx={{
                display: 'block',
                maxHeight: { xs: '55vh', md: 520 },
                minHeight: 360,
                overflowX: 'auto',
                overflowY: 'scroll',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(31, 91, 64, 0.45) rgba(31, 91, 64, 0.08)',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { width: 10, height: 10 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(31, 91, 64, 0.4)',
                  borderRadius: 8,
                  border: '2px solid rgba(31, 91, 64, 0.06)'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(31, 91, 64, 0.08)',
                  borderRadius: 8
                }
              }}
            >
              <Table stickyHeader sx={{ minWidth: 1300 }}>
                <TableHead>
                  <TableRow>
                    {COLS.map((col, i) => (
                      <TableCell
                        key={i}
                        align={col.align}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          bgcolor: 'grey.50',
                          whiteSpace: 'nowrap',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          py: 1.5
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                        <CircularProgress size={36} />
                      </TableCell>
                    </TableRow>
                  ) : products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLS.length} align="center" sx={{ py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                          {hasFetched ? (search ? `No products match "${search}"` : 'No products found') : 'Loading products…'}
                        </Typography>
                        {hasFetched && !search && (
                          <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => openProductForm()}>
                            Add First Product
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((p, idx) => (
                      <ProductRow
                        key={p.id}
                        p={p}
                        idx={idx}
                        onEdit={openProductForm}
                        onDelete={(prod) => setDelDialog({ open: true, id: prod.id, name: prod.name })}
                        onToggleStatus={handleToggleStatus}
                        togglingId={togglingId}
                      />
                    ))
                  )}
                  {!loading && products.length > 0 && (
                    <TableRow>
                      <TableCell ref={loadMoreSentinelRef} colSpan={COLS.length} align="center" sx={{ py: 1.5, border: 0 }}>
                        {loadingMore ? (
                          <CircularProgress size={24} />
                        ) : !hasMore ? (
                          <Typography variant="caption" color="text.secondary">
                            All {total} {pluralize(total, 'product')} loaded
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Scroll for more
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </MainCard>

      <ProductFilteredByStoreForm
        open={formModal.open}
        productId={formModal.id}
        storeId={storeId}
        storeName={decodeURIComponent(storeName || '')}
        initialCategoryId={filterCategoryId}
        onSave={loadInitial}
        onClose={() => setFormModal({ open: false, id: null })}
      />

      {/* delete dialog */}
      <Dialog
        open={delDialog.open}
        onClose={() => {
          if (!deleting) setDelDialog({ open: false, id: null, name: '' });
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{delDialog.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDelDialog({ open: false, id: null, name: '' })} disabled={deleting} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            size="small"
            startIcon={deleting ? <CircularProgress size={14} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={importOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Import Products from Excel</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload an Excel file containing product data to import products into the selected store.
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block">
                Supported formats: <strong>.xlsx, .xls</strong>
              </Typography>
            </Box>

            {/* File selection box */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: importFile ? 'primary.main' : 'divider',
                bgcolor: importFile ? 'rgba(31, 91, 64, 0.02)' : 'grey.50',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              component="label"
            >
              <input
                type="file"
                accept=".xlsx, .xls"
                hidden
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setImportFile(e.target.files[0]);
                    setImportErrors([]);
                    setImportStats(null);
                  }
                }}
              />
              <Stack alignItems="center" spacing={1}>
                <FileUploadOutlinedIcon sx={{ fontSize: 36, color: importFile ? 'primary.main' : 'text.secondary' }} />
                {importFile ? (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                      {importFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(importFile.size / 1024).toFixed(1)} KB — Click to change file
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Click to choose file or drag & drop here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select an .xlsx or .xls file
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Import Stats display (Partial Success / Summary) */}
            {importStats && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                  Import Results Summary
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Total Rows: ${importStats.total_rows}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                  <Chip label={`Success: ${importStats.success_count}`} size="small" color="success" sx={{ fontWeight: 600 }} />
                  <Chip
                    label={`Failed: ${importStats.failed_count}`}
                    size="small"
                    color={importStats.failed_count > 0 ? 'error' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Paper>
            )}

            {/* Error display section */}
            {importErrors.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="error.main" mb={1}>
                  Validation Errors ({importErrors.length})
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    p: 1.5,
                    bgcolor: 'error.lighter',
                    borderColor: 'error.light',
                    borderRadius: 1.5
                  }}
                >
                  <Stack spacing={0.75}>
                    {importErrors.map((err, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        color="error.dark"
                        display="block"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      >
                        {formatImportError(err)}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseImportDialog} disabled={importLoading} variant="outlined" size="small">
            {importStats && importStats.failed_count > 0 ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={handleImportSubmit}
            disabled={!importFile || importLoading}
            variant="contained"
            color="primary"
            size="small"
            startIcon={importLoading ? <CircularProgress size={14} color="inherit" /> : <FileUploadOutlinedIcon />}
          >
            {importLoading ? 'Uploading…' : 'Upload & Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
